import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EyeOff } from 'lucide-react';
import { getLiveHost } from '@api-client/api-client';
import { liveHostKey } from '@lib/cache/polls';
import type { PollRead } from '@models/api/poll';
import { routes } from '@routes';
import { LIVE_POLL_COMPOSER_COPY } from './livePollCopy';

export interface LivePollNotStartedCardProps {
  /** Question 1's text: the post's title (CONTRACT § 4.1 live). */
  title: string;
  poll: Pick<PollRead, 'questions_count' | 'options' | 'live_code'>;
}

/** One line of the question list: "{n}. {question} " and the grey "· {k} options". */
const QuestionLine: React.FC<{ n: number; text: string; options: number }> = ({
  n,
  text,
  options,
}) => (
  <li className="text-sm leading-5 text-mb-gray-dark wrap-break-word">
    {LIVE_POLL_COMPOSER_COPY.foldedNumber(n, text)}
    <span className="text-mb-grey-300">{LIVE_POLL_COMPOSER_COPY.foldedOptions(options)}</span>
  </li>
);

/**
 * L5, a saved live poll before Start (TECH-867 § Decided "It enters the feed at Start"; CONTRACT
 * § 10 F3): the author's draft, the only reader the server lets see it. The one state chip "Not
 * started" (RESOLUTIONS L5), "Live poll · {n} questions", the question list (from the host read,
 * which carries every question; question 1 from the post until it lands), "Only you can see this
 * until you start it." and "Run live poll" (44px, L8) to the host view. No Share and no footer;
 * the card's byline reads "· saved {age} ago" (QuestionCard).
 */
export const LivePollNotStartedCard: React.FC<LivePollNotStartedCardProps> = ({ title, poll }) => {
  const navigate = useNavigate();
  const code = poll.live_code;
  const { data: host } = useQuery({
    queryKey: liveHostKey(code ?? ''),
    queryFn: async ({ signal }) => (await getLiveHost(code ?? '', { signal })).data,
    enabled: code !== null,
    staleTime: 30_000,
  });
  const questions = host?.questions ?? [
    { position: 0, text: title, options_count: poll.options.length },
  ];
  const count = host?.questions.length ?? poll.questions_count;

  return (
    <div className="flex flex-col gap-3" data-testid="live-poll-not-started">
      <div className="flex items-center gap-2">
        <span className="rounded-full bg-mb-beige px-2 py-0.5 text-[11px] leading-[14px] font-bold tracking-[0.06em] text-mb-gray-dark uppercase">
          {LIVE_POLL_COMPOSER_COPY.notStarted}
        </span>
        <span className="text-xs leading-4 font-medium text-mb-dark-gray">
          {LIVE_POLL_COMPOSER_COPY.livePollQuestions(count)}
        </span>
      </div>
      <ol className="flex flex-col gap-1.5">
        {questions.map(question => (
          <QuestionLine
            key={question.position}
            n={question.position + 1}
            text={question.text}
            options={question.options_count}
          />
        ))}
      </ol>
      <div className="flex flex-col gap-2.5">
        <p className="flex items-center gap-1.5 text-xs leading-4 font-medium text-mb-dark-gray">
          <EyeOff className="size-3.5 shrink-0" strokeWidth={2} aria-hidden="true" />
          {LIVE_POLL_COMPOSER_COPY.onlyYouUntilStart}
        </p>
        {code && (
          <button
            type="button"
            data-testid="run-live-poll"
            onClick={event => {
              event.stopPropagation();
              navigate(routes.liveHost(code));
            }}
            className="h-11 w-full rounded-full bg-mb-yellow text-sm leading-[18px] font-medium text-black shadow-[0_4px_5px_#FFCA2880] hover:bg-mb-yellow focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2 focus-visible:outline-hidden"
          >
            {LIVE_POLL_COMPOSER_COPY.runLivePoll}
          </button>
        )}
      </div>
    </div>
  );
};
